/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-24 13:44:49
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { I18n } from '@shopify/react-i18n';
import { getJSON } from 'redux-api-middleware';
import { processRes } from 'src/helpers/actions-helper';
import type { FilterTreeType } from 'src/models/filter-tree';

import apiService from 'src/services/api-service';
import {
  GET_FILTER_TREE_REQUEST,
  GET_FILTER_TREE_SUCCESS,
  GET_FILTER_TREE_FAILURE,
  CREATE_FILTER_TREE_REQUEST,
  CREATE_FILTER_TREE_SUCCESS,
  CREATE_FILTER_TREE_FAILURE,
  UPDATE_FILTER_TREE_REQUEST,
  UPDATE_FILTER_TREE_SUCCESS,
  UPDATE_FILTER_TREE_FAILURE,
  GET_ALL_FILTER_OPTIONS_REQUEST,
  GET_ALL_FILTER_OPTIONS_SUCCESS,
  GET_ALL_FILTER_OPTIONS_FAILURE
} from 'stores/flow-type/filter-tree/filter-tree.type';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to get filter tree api
 * @return {object} The RSAA object for API middleware
 */
export const getFilterTree = (filterId: string, i18n: I18n) => {
  const params = { filterId };
  return apiService.sendRequest('admin-v3.filter-tree.get-filter-tree', { params }, [
    GET_FILTER_TREE_REQUEST,
    {
      type: GET_FILTER_TREE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_FILTER_TREE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.FilterTree.Actions.getFilterTree'),
          i18n
        )
    }
  ]);
};

/**
 * call to create filter tree api
 * @return {object} The RSAA object for API middleware
 */
export const createFilterTree = (data: FilterTreeType, i18n: I18n) => {
  // Prepare Body
  let dataBody = { ...data };
  let { collections } = dataBody;
  const scope = collections ? _.map(collections, item => item.value.toString()) : [];
  dataBody.options = { name: dataBody.name, scope };

  if (collections) {
    dataBody.collections = _.map(collections, item => ({ id: item.value, title: item.label }));
  }

  return apiService.sendRequest('admin-v3.filter-tree.create-filter-tree', { body: dataBody }, [
    CREATE_FILTER_TREE_REQUEST,
    {
      type: CREATE_FILTER_TREE_SUCCESS,
      payload: async (action, state, res) => {
        const dataResult = await processRes(
          res,
          'custom-with-sync',
          i18n.translate('AdminHTML.FilterTree.Actions.createFilterTree_Success'),
          i18n
        );

        mixpanelV3Helper.trackEventData('Add New Filter Tree', {
          feature: 'Manage Filter Tree',
          optionTypes: dataBody?.filterOptions?.map(e => e.filterType),
          noFilterOptions: dataBody?.filterOptions?.length,
          collections: dataBody?.collections?.map(e => e.title)
        });

        // Track Full Story event
        trackFSEvent('Add New Filter Tree', {
          feature: 'Manage Filter Tree',
          optionTypes: dataBody?.filterOptions?.map(e => e.filterType),
          noFilterOptions: dataBody?.filterOptions?.length,
          collections: dataBody?.collections?.map(e => e.title)
        });

        return { ...dataResult, filterTree: data };
      }
    },
    {
      type: CREATE_FILTER_TREE_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.FilterTree.Actions.createFilterTree_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to update filter tree api
 * @return {object} The RSAA object for API middleware
 */
export const updateFilterTree = (filterId: string, data: FilterTreeType, i18n: I18n) => {
  const params = { filterId };
  const { isDefault, isDefaultSearch } = data;
  // Prepare Body
  let dataBody = { ...data };
  let { collections } = dataBody;

  let scope = [];
  // Set scope for default filter and default search filter
  if (isDefault || isDefaultSearch) {
    scope = ['0'];
  } else {
    scope = collections ? _.map(collections, item => item.value.toString()) : [];
  }

  dataBody.options = { name: dataBody.name, scope };

  if (collections) {
    dataBody.collections = _.map(collections, item => ({ id: item.value, title: item.label }));
  }

  return apiService.sendRequest(
    'admin-v3.filter-tree.update-filter-tree',
    { body: dataBody, params },
    [
      UPDATE_FILTER_TREE_REQUEST,
      {
        type: UPDATE_FILTER_TREE_SUCCESS,
        payload: async (action, state, res) => {
          const dataResult = await processRes(
            res,
            'custom-with-sync',
            i18n.translate('AdminHTML.FilterTree.Actions.updateFilterTree_Success'),
            i18n
          );

          mixpanelV3Helper.trackEventData('Edit Filter Tree', {
            feature: 'Manage Filter Tree',
            optionTypes: dataBody?.filterOptions?.map(e => e.filterType),
            noFilterOptions: dataBody?.filterOptions?.length,
            collections: dataBody?.collections?.map(e => e.title)
          });

          // Track Full Story event
          trackFSEvent('Edit Filter Tree', {
            feature: 'Manage Filter Tree',
            optionTypes: dataBody?.filterOptions?.map(e => e.filterType),
            noFilterOptions: dataBody?.filterOptions?.length,
            collections: dataBody?.collections?.map(e => e.title)
          });

          return { ...dataResult, filterTree: data };
        }
      },
      {
        type: UPDATE_FILTER_TREE_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.FilterTree.Actions.updateFilterTree_Failure'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to get filter tree api
 * @return {object} The RSAA object for API middleware
 */
export const getAllFilterOptions = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.filter-tree.get-all-filter-options', {}, [
    GET_ALL_FILTER_OPTIONS_REQUEST,
    {
      type: GET_ALL_FILTER_OPTIONS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_ALL_FILTER_OPTIONS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.FilterTree.Actions.getAllFilterOptions'),
          i18n
        )
    }
  ]);
};
